// ===============================================
// 📚 DOCUMENTATION VIEWER - ACTUALIZADO PARA ADMINDASHBOARD
// ===============================================

import React, { useState, useEffect, useRef } from 'react';
import { 
  FileText, 
  Download, 
  Search, 
  ZoomIn, 
  ZoomOut,
  RotateCw,
  RotateCcw,
  Maximize,
  Minimize,
  ChevronLeft,
  ChevronRight,
  Filter,
  Folder,
  FolderOpen,
  Book,
  Image,
  Video,
  Monitor,
  Wrench,
  Settings,
  AlertCircle,
  CheckCircle,
  Clock,
  Star,
  Bookmark,
  Share2,
  Print,
  Eye,
  RefreshCw,
  X,
  Grid,
  List,
  Play,
  Pause,
  Tag,
  User,
  Calendar
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useI18n } from '../../hooks/useI18n';

// ===============================================
// 🔧 INTERFACES ACTUALIZADAS
// ===============================================

// ✅ INTERFACE COMPATIBLE CON ADMINDASHBOARD
interface DocumentItem {
  id: string;
  title: string;
  description: string;
  type: 'manual' | 'diagram' | 'video' | 'guide' | 'specification' | 'troubleshooting';
  format: 'pdf' | 'html' | 'video' | 'image' | 'interactive';
  category: 'operation' | 'maintenance' | 'repair' | 'parts' | 'safety' | 'installation';
  motor?: string;
  configuration?: string;
  component?: string;
  filename: string;
  file_path: string;
  file_url: string;
  thumbnail_url?: string;
  file_size: string;
  file_type: string;
  pages?: number;
  duration?: string;
  language: 'es' | 'en' | 'pt';
  version: string;
  created_at: string;
  updated_at: string;
  uploaded_by: string;
  downloads: number;
  views: number;
  rating: number;
  is_featured: boolean;
  is_premium: boolean;
  is_public: boolean;
  access_level: 'public' | 'registered' | 'premium';
  tags: string[];
  related_documents: string[];
}

interface ViewerState {
  currentDocument: DocumentItem | null;
  currentPage: number;
  totalPages: number;
  zoom: number;
  rotation: number;
  isFullscreen: boolean;
  isLoading: boolean;
}

// ===============================================
// 📊 COMPONENTE PRINCIPAL
// ===============================================

const DocumentationViewer: React.FC = () => {
  const { isDark } = useTheme();
  const { t } = useI18n();
  const [documents, setDocuments] = useState<DocumentItem[]>([]);
  const [filteredDocuments, setFilteredDocuments] = useState<DocumentItem[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedType, setSelectedType] = useState<string>('all');
  const [selectedMotor, setSelectedMotor] = useState<string>('all');
  const [selectedLanguage, setSelectedLanguage] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showFilters, setShowFilters] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const [viewer, setViewer] = useState<ViewerState>({
    currentDocument: null,
    currentPage: 1,
    totalPages: 1,
    zoom: 100,
    rotation: 0,
    isFullscreen: false,
    isLoading: false
  });

  const viewerRef = useRef<HTMLDivElement>(null);

  // ===============================================
  // 🎨 CLASES DE ESTILO
  // ===============================================

  const cardClasses = `rounded-lg shadow-md transition-all duration-200 ${
    isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
  }`;

  const textClasses = isDark ? 'text-white' : 'text-gray-800';
  const subTextClasses = isDark ? 'text-gray-400' : 'text-gray-600';
  const borderClasses = isDark ? 'border-gray-600' : 'border-gray-300';
  const inputClasses = `w-full px-3 py-2 border rounded-lg ${
    isDark 
      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
  } focus:ring-2 focus:ring-blue-500 focus:border-transparent`;

  // ===============================================
  // 📡 FUNCIONES DE API
  // ===============================================

  const loadDocuments = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // ✅ CARGAR DESDE LA MISMA API QUE ADMINDASHBOARD
      const response = await fetch('/api/admin/documents', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || localStorage.getItem('kikai_token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`Error loading documents: ${response.status}`);
      }
      
      const data = await response.json();
      
      // ✅ FILTRAR SOLO DOCUMENTOS PÚBLICOS Y NORMALIZAR DATOS
      const publicDocuments = data.documents?.filter((doc: DocumentItem) => doc.is_public).map((doc: DocumentItem) => ({
        ...doc,
        tags: Array.isArray(doc.tags) ? doc.tags : [],
        related_documents: Array.isArray(doc.related_documents) ? doc.related_documents : []
      })) || [];
      
      setDocuments(publicDocuments);
      setFilteredDocuments(publicDocuments);
      
    } catch (error) {
      console.error('Error loading documents:', error);
      setError(error instanceof Error ? error.message : 'Error desconocido');
    } finally {
      setLoading(false);
    }
  };

  const incrementDownloadCount = async (documentId: string) => {
    try {
      await fetch(`/api/documents/${documentId}/download`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || localStorage.getItem('kikai_token')}`
        }
      });
    } catch (error) {
      console.error('Error incrementing download count:', error);
    }
  };

  const incrementViewCount = async (documentId: string) => {
    try {
      await fetch(`/api/documents/${documentId}/view`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || localStorage.getItem('kikai_token')}`
        }
      });
    } catch (error) {
      console.error('Error incrementing view count:', error);
    }
  };

  // ===============================================
  // 🔍 FUNCIONES DE FILTRADO Y BÚSQUEDA
  // ===============================================

  const filterDocuments = () => {
    let filtered = [...documents];

    // Filtro por búsqueda
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(doc => 
        doc.title.toLowerCase().includes(searchLower) ||
        doc.description.toLowerCase().includes(searchLower) ||
        (doc.tags && Array.isArray(doc.tags) && doc.tags.some(tag => tag.toLowerCase().includes(searchLower))) ||
        (doc.motor && doc.motor.toLowerCase().includes(searchLower)) ||
        (doc.component && doc.component.toLowerCase().includes(searchLower)) ||
        (doc.configuration && doc.configuration.toLowerCase().includes(searchLower))
      );
    }

    // Filtros específicos
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(doc => doc.category === selectedCategory);
    }
    
    if (selectedType !== 'all') {
      filtered = filtered.filter(doc => doc.type === selectedType);
    }
    
    if (selectedMotor !== 'all') {
      filtered = filtered.filter(doc => doc.motor === selectedMotor);
    }
    
    if (selectedLanguage !== 'all') {
      filtered = filtered.filter(doc => doc.language === selectedLanguage);
    }

    // Ordenar por destacados primero, luego por fecha
    filtered.sort((a, b) => {
      if (a.is_featured && !b.is_featured) return -1;
      if (!a.is_featured && b.is_featured) return 1;
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });

    setFilteredDocuments(filtered);
  };

  // ===============================================
  // 🎯 FUNCIONES DE DOCUMENTO
  // ===============================================

  const handleDocumentClick = async (document: DocumentItem) => {
    await incrementViewCount(document.id);
    
    // Actualizar contador local
    setDocuments(prev => prev.map(doc => 
      doc.id === document.id 
        ? { ...doc, views: (doc.views || 0) + 1 }
        : doc
    ));

    setViewer({
      currentDocument: document,
      currentPage: 1,
      totalPages: document.pages || 1,
      zoom: 100,
      rotation: 0,
      isFullscreen: false,
      isLoading: true
    });
  };

  const handleDownload = async (document: DocumentItem) => {
    try {
      await incrementDownloadCount(document.id);
      
      // Actualizar contador local
      setDocuments(prev => prev.map(doc => 
        doc.id === document.id 
          ? { ...doc, downloads: (doc.downloads || 0) + 1 }
          : doc
      ));

      // Descargar archivo
      const link = window.document.createElement('a');
      link.href = document.file_url || `/api/documents/${document.id}/download`;
      link.download = document.filename;
      link.click();
    } catch (error) {
      console.error('Error downloading document:', error);
    }
  };

  // ===============================================
  // 🔄 EFECTOS
  // ===============================================

  useEffect(() => {
    loadDocuments();
  }, []);

  useEffect(() => {
    filterDocuments();
  }, [searchTerm, selectedCategory, selectedType, selectedMotor, selectedLanguage, documents]);

  // ===============================================
  // 🎨 FUNCIONES DE UTILIDAD
  // ===============================================

  const getFileIcon = (type: string, format: string) => {
    switch (format) {
      case 'pdf': return FileText;
      case 'video': return Video;
      case 'image': return Image;
      default: return Book;
    }
  };

  const getCategoryColor = (category: string) => {
    const colors = {
      operation: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
      maintenance: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
      repair: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
      parts: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
      safety: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
      installation: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300'
    };
    return colors[category] || colors.operation;
  };

  const formatFileSize = (bytes: string | number) => {
    const size = typeof bytes === 'string' ? parseInt(bytes) : bytes;
    if (size === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(size) / Math.log(k));
    return parseFloat((size / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getUniqueValues = (field: keyof DocumentItem) => {
    const values = documents
      .map(doc => doc[field])
      .filter(value => value && value !== '' && value !== null && value !== undefined)
      .filter((value, index, self) => self.indexOf(value) === index);
    return values.sort();
  };

  // ===============================================
  // 🎯 RENDERIZADO PRINCIPAL
  // ===============================================

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* Header */}
      <div className={`${cardClasses} p-6 mb-6`}>
        <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <FileText className="w-6 h-6 text-blue-600" />
              <h1 className={`text-2xl font-bold ${textClasses}`}>
                {t('docs.title', 'Documentación Técnica')}
              </h1>
            </div>
            <p className={subTextClasses}>
              {t('docs.description', 'Manuales, diagramas y documentos técnicos para motores Caterpillar')}
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
              className={`p-2 rounded-lg border  bg-blue-600 text-white ${borderClasses} ${
                isDark 
                  ? 'hover:bg-blue-700' 
                  : 'hover:bg-gray-50'
              } transition-colors`}
            >
              {viewMode === 'grid' ? <List className="w-5 h-5" /> : <Grid className="w-5 h-5" />}
            </button>
            
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2  bg-blue-600 text-white rounded-lg border ${borderClasses} ${
                isDark 
                  ? 'hover:bg-blue-700' 
                  : 'hover:bg-gray-50'
              } transition-colors`}
            >
              <Filter className="w-4 h-4" />
              {t('docs.filters', 'Filtros')}
            </button>
            
            <button
              onClick={loadDocuments}
              disabled={loading}
              className={`p-2 rounded-lg border  bg-blue-600 text-white ${borderClasses} ${
                isDark 
                  ? 'hover:bg-blue-700' 
                  : 'hover:bg-gray-50'
              } transition-colors`}
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Búsqueda principal */}
        <div className="mt-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder={t('docs.searchPlaceholder', 'Buscar documentos por título, descripción, motor, componente...')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`pl-10 ${inputClasses}`}
            />
          </div>
        </div>

        {/* Panel de filtros expandible */}
        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
            <div>
              <label className={`block text-sm font-medium ${textClasses} mb-2`}>
                {t('docs.category', 'Categoría')}
              </label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className={inputClasses}
              >
                <option value="all">{t('docs.allCategories', 'Todas las categorías')}</option>
                <option value="operation">{t('docs.categories.operation', 'Operación')}</option>
                <option value="maintenance">{t('docs.categories.maintenance', 'Mantenimiento')}</option>
                <option value="repair">{t('docs.categories.repair', 'Reparación')}</option>
                <option value="parts">{t('docs.categories.parts', 'Piezas')}</option>
                <option value="safety">{t('docs.categories.safety', 'Seguridad')}</option>
                <option value="installation">{t('docs.categories.installation', 'Instalación')}</option>
              </select>
            </div>
            
            <div>
              <label className={`block text-sm font-medium ${textClasses} mb-2`}>
                {t('docs.type', 'Tipo')}
              </label>
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className={inputClasses}
              >
                <option value="all">{t('docs.allTypes', 'Todos los tipos')}</option>
                <option value="manual">{t('docs.types.manual', 'Manual')}</option>
                <option value="diagram">{t('docs.types.diagram', 'Diagrama')}</option>
                <option value="video">{t('docs.types.video', 'Video')}</option>
                <option value="guide">{t('docs.types.guide', 'Guía')}</option>
                <option value="specification">{t('docs.types.specification', 'Especificación')}</option>
                <option value="troubleshooting">{t('docs.types.troubleshooting', 'Solución de problemas')}</option>
              </select>
            </div>
            
            <div>
              <label className={`block text-sm font-medium ${textClasses} mb-2`}>
                {t('docs.motor', 'Motor')}
              </label>
              <select
                value={selectedMotor}
                onChange={(e) => setSelectedMotor(e.target.value)}
                className={inputClasses}
              >
                <option value="all">{t('docs.allMotors', 'Todos los motores')}</option>
                {getUniqueValues('motor').map(motor => (
                  <option key={motor} value={motor}>{motor}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className={`block text-sm font-medium ${textClasses} mb-2`}>
                {t('docs.language', 'Idioma')}
              </label>
              <select
                value={selectedLanguage}
                onChange={(e) => setSelectedLanguage(e.target.value)}
                className={inputClasses}
              >
                <option value="all">{t('docs.allLanguages', 'Todos los idiomas')}</option>
                <option value="es">Español</option>
                <option value="en">English</option>
                <option value="pt">Português</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Estado de carga */}
      {loading && (
        <div className={`${cardClasses} p-8 text-center`}>
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className={textClasses}>{t('docs.loading', 'Cargando documentos...')}</p>
        </div>
      )}

      {/* Estado de error */}
      {error && (
        <div className={`${cardClasses} p-6 mb-6`}>
          <div className="flex items-center gap-3 text-red-600">
            <AlertCircle className="w-5 h-5" />
            <span>{t('docs.error', 'Error')}: {error}</span>
          </div>
        </div>
      )}

      {/* Estadísticas */}
      {!loading && !error && (
        <div className={`${cardClasses} p-4 mb-6`}>
          <div className="flex items-center justify-between">
            <div className={`text-sm ${subTextClasses}`}>
              {t('docs.showingResults', 'Mostrando {{showing}} de {{total}} documentos', {
                showing: filteredDocuments.length,
                total: documents.length
              })}
            </div>
            <div className="flex items-center gap-4 text-sm">
              <div className={subTextClasses}>
                {t('docs.totalDownloads', 'Total descargas: {{count}}', {
                  count: documents.reduce((sum, doc) => sum + (doc.downloads || 0), 0)
                })}
              </div>
              <div className={subTextClasses}>
                {t('docs.totalViews', 'Total visualizaciones: {{count}}', {
                  count: documents.reduce((sum, doc) => sum + (doc.views || 0), 0)
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Lista de documentos */}
      {!loading && !error && (
        <div className={viewMode === 'grid' 
          ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6' 
          : 'space-y-4'
        }>
          {filteredDocuments.map((document) => {
            const FileIcon = getFileIcon(document.type, document.format);
            
            return (
              <div
                key={document.id}
                className={`${cardClasses} hover:shadow-lg transition-all duration-200 ${
                  document.is_featured ? 'ring-2 ring-yellow-400' : ''
                }`}
              >
                {/* Header del documento */}
                <div className="p-4">
                  <div className="flex items-start gap-3 mb-3">
                    <div className={`p-2 rounded-lg ${
                      isDark ? 'bg-gray-700' : 'bg-gray-100'
                    }`}>
                      <FileIcon className="w-6 h-6 text-blue-600" />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className={`font-semibold ${textClasses} truncate`}>
                          {document.title}
                        </h3>
                        {document.is_featured && (
                          <Star className="w-4 h-4 text-yellow-500 flex-shrink-0" />
                        )}
                        {document.is_premium && (
                          <div className="px-2 py-1 text-xs bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300 rounded-full flex-shrink-0">
                            Premium
                          </div>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`px-2 py-1 text-xs rounded-full ${getCategoryColor(document.category)}`}>
                          {document.category}
                        </span>
                        <span className={`text-xs ${subTextClasses}`}>
                          v{document.version}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Descripción */}
                  <p className={`text-sm ${subTextClasses} mb-3 line-clamp-2`}>
                    {document.description}
                  </p>

                  {/* Metadatos */}
                  <div className="space-y-2 mb-4">
                    {document.motor && (
                      <div className="flex items-center gap-2 text-sm">
                        <Settings className="w-4 h-4 text-gray-400" />
                        <span className={subTextClasses}>Motor: {document.motor}</span>
                      </div>
                    )}
                    
                    {document.configuration && (
                      <div className="flex items-center gap-2 text-sm">
                        <Wrench className="w-4 h-4 text-gray-400" />
                        <span className={subTextClasses}>Config: {document.configuration}</span>
                      </div>
                    )}
                    
                    {document.component && (
                      <div className="flex items-center gap-2 text-sm">
                        <Monitor className="w-4 h-4 text-gray-400" />
                        <span className={subTextClasses}>Componente: {document.component}</span>
                      </div>
                    )}
                  </div>

                  {/* Tags */}
                  {document.tags && Array.isArray(document.tags) && document.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-4">
                      {document.tags.slice(0, 3).map((tag, index) => (
                        <span
                          key={index}
                          className={`px-2 py-1 text-xs rounded-md ${
                            isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {tag}
                        </span>
                      ))}
                      {document.tags.length > 3 && (
                        <span className={`text-xs ${subTextClasses}`}>
                          +{document.tags.length - 3} más
                        </span>
                      )}
                    </div>
                  )}

                  {/* Estadísticas */}
                  <div className="flex items-center justify-between text-sm mb-4">
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-1">
                        <Eye className="w-4 h-4 text-gray-400" />
                        <span className={subTextClasses}>{document.views || 0}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Download className="w-4 h-4 text-gray-400" />
                        <span className={subTextClasses}>{document.downloads || 0}</span>
                      </div>
                    </div>
                    
                    <div className={`text-xs ${subTextClasses}`}>
                      {formatFileSize(document.file_size)}
                    </div>
                  </div>

                  {/* Información adicional */}
                  <div className={`text-xs ${subTextClasses} mb-4`}>
                    <div className="flex items-center gap-2 mb-1">
                      <User className="w-3 h-3" />
                      <span>Por: {document.uploaded_by}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="w-3 h-3" />
                      <span>{new Date(document.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>

                  {/* Acciones */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleDocumentClick(document)}
                      className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <Eye className="w-4 h-4" />
                      Ver
                    </button>
                    
                    <button
                      onClick={() => handleDownload(document)}
                      className={`flex items-center justify-center gap-2 px-3 py-2 border bg-blue-600 ${borderClasses} rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors`}
                    >
                      <Download className="w-4 h-4" />
                    </button>
                    
                    <button
                      className={`flex items-center justify-center gap-2 px-3 py-2 border bg-blue-600 ${borderClasses} rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors`}
                    >
                      <Bookmark className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Mensaje cuando no hay documentos */}
      {!loading && !error && filteredDocuments.length === 0 && (
        <div className={`${cardClasses} p-8 text-center`}>
          <FileText className="w-16 h-16 mx-auto mb-4 text-gray-400" />
          <h3 className={`text-lg font-semibold ${textClasses} mb-2`}>
            {t('docs.noResults', 'No se encontraron documentos')}
          </h3>
          <p className={subTextClasses}>
            {t('docs.tryAdjustingFilters', 'Intenta ajustar los filtros o la búsqueda para encontrar más resultados.')}
          </p>
        </div>
      )}

      {/* Visor de documentos */}
      {viewer.currentDocument && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className={`${cardClasses} max-w-4xl max-h-[90vh] w-full mx-4 flex flex-col`}>
            {/* Header del visor */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <div>
                <h3 className={`font-semibold ${textClasses}`}>
                  {viewer.currentDocument.title}
                </h3>
                <p className={`text-sm ${subTextClasses}`}>
                  {viewer.currentDocument.filename}
                </p>
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleDownload(viewer.currentDocument)}
                  className="p-2 rounded-lg hover:bg-blue-900 bg-blue-600 text-white dark:hover:bg-blue-700 transition-colors"
                >
                  <Download className="w-5 h-5" />
                </button>
                
                <button
                  onClick={() => setViewer(prev => ({ ...prev, currentDocument: null }))}
                  className="p-2 rounded-lg hover:bg-blue-900 bg-blue-600 text-white dark:hover:bg-blue-700 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            {/* Contenido del visor */}
            <div className="flex-1 p-4 overflow-hidden">
              {viewer.currentDocument.format === 'pdf' ? (
                <iframe
                  src={viewer.currentDocument.file_url}
                  className="w-full h-full rounded-lg"
                  title={viewer.currentDocument.title}
                />
              ) : viewer.currentDocument.format === 'video' ? (
                <video
                  src={viewer.currentDocument.file_url}
                  controls
                  className="w-full h-full rounded-lg"
                />
              ) : viewer.currentDocument.format === 'image' ? (
                <img
                  src={viewer.currentDocument.file_url}
                  alt={viewer.currentDocument.title}
                  className="w-full h-full object-contain rounded-lg"
                />
              ) : (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <FileText className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                    <p className={textClasses}>
                      Vista previa no disponible para este tipo de archivo
                    </p>
                    <button
                      onClick={() => handleDownload(viewer.currentDocument)}
                      className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Descargar archivo
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentationViewer;